import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import OverviewMenuButtons from "../../components/OverviewMenuButtonsWithBorder";
import BlueTile from "../../components/BlueTile";
import { ArrowBack } from "@mui/icons-material";
import InputDirectoryForm from "../InvoiceAgents/InputDirectory";
import OutPutDirectory from "../InvoiceAgents/OutPutDirectory";
import FieldsDirectoryScreen from "../InvoiceAgents/FieldsDirectoryScreen";
import "../../css/OverviewMenuButtonsWithBorder.css";
import AppLayout from "../../components/AppLayout";
import ReviewScreen from "../InvoiceAgents/ReviewScreen";
import OutputReview from "../InvoiceAgents/OutputReview";
import AgenName from '../../components/AgentName'
import DeployAIBot from "../../components/DeployAIBot";
import CreateAgentLoader from "../../components/CreateAgentLoader";
import Prompts from "./Prompts";
import CsvReviewScreen from "./CsvReviewScreen";
import DocumentUpload from "./DocumentUpload";
import { getImages, getProfileImageWithStyle } from '../../utills/lib';
import { useInvoiceAgents } from './hooks/useInvoiceAgents';
import { invoiceAgentsStyles, getButtonStyles, getBlueTileStyles, getResponsiveStyles } from './styles/invoiceAgentsStyles';
import FooterButtons from './components/FooterButtons';
import { setAgentImage, resetAgentImage } from "../../redux/slices/AgentImageSlice";
import { saveFormDetails, resetFormDetails } from "../../redux/slices/CreateAgentSlice";
import { FORM_KEYS } from "../../utills/constant";

const OverviewMenuButtonsWithBorder = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    // State
    selected,
    AgenNamePopUp,
    dialogOpen,
    showCreateLoader,
    showDeployLoader,
    snackbar,
    error,
    parentRef,
    receivedData,
    backDisabled,

    // Redux state
    csvData,
    formDetails,
    createAgentLoading,
    createAgent,
    slugName,
    createAgentError,
    outputOcrFields,
    deployAgentLoading,
    deployAgentError,
    deployAgent,
    profileDetails,
    statisticInfo,
    statisticInfoLoading,

    // Actions
    setAgenNamePopUp,
    setShowCreateLoader,
    setShowDeployLoader,
    setErrors,
    handleUpdateDetails,
    handleCreateAgent,
    handleButtonClick,
    handleDialogOpen,
    handleDialogClose,
    handleCloseSnackbar,
    validateAndUpdate,
    validateAndProceed,
    handleBackButton,
    handleRunClick,

    // Computed values
    buttons,
    getCsvLoading,
  } = useInvoiceAgents();

  const isEditMode = !!receivedData?.steps;
  const buttonStyles = getButtonStyles(isEditMode);
  const blueTileStyles = getBlueTileStyles(receivedData);
  const responsiveStyles = getResponsiveStyles(isMobile, isTablet);

  const dispatch = useDispatch();
  const location = useLocation();
  const agentImages = useSelector((state) => state.AgentImageSlice.images);
  const slug = receivedData?.slug;

  console.log("receivedData", receivedData)

  const extractCleanFileName = (imagePath) => {
    if (!imagePath) return "";
    const file = imagePath.split("/").pop().split("?")[0];
    const cleaned = file.replace(/\.[a-f0-9]{8,}\./, ".");
    return cleaned;
  };


  let agentImageSrc = '';

  if (receivedData?.profile_pic) {
    agentImageSrc = getProfileImageWithStyle(receivedData.profile_pic).src;
  } else {
    // check if image already stored in redux
    agentImageSrc = agentImages[slug];

    if (!agentImageSrc && slug) {
      // assign new one via getImages (same logic as before)
      const newImage = getImages(slug);
      dispatch(setAgentImage({ slug, image: newImage }));
      dispatch(saveFormDetails({ key: FORM_KEYS.PROFILE_PIC, value: extractCleanFileName(newImage) }));
      agentImageSrc = newImage;
    }
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem("isReloading", "true");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      const isReloading = sessionStorage.getItem("isReloading");
      if (!isReloading) {
        dispatch(resetAgentImage());
      }
      sessionStorage.removeItem("isReloading");
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dispatch, location.pathname]);

  const redirectPage = () => {
    dispatch(resetAgentImage());
    navigate(-1);
  };

  return (
    <AppLayout>
      <div ref={parentRef} className="overview-menu-container" style={invoiceAgentsStyles.container}>
        <Box className="header-section" style={invoiceAgentsStyles.headerSection}>
          <Box
            onClick={redirectPage}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#f8fafc',
              border: '2px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: '#1976d2',
                borderColor: '#1976d2',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                '& .arrow-icon': {
                  color: '#ffffff'
                }
              }
            }}
          >
            <ArrowBack
              className="arrow-icon"
              sx={{
                fontSize: '1.2rem',
                color: '#64748b',
                transition: 'color 0.3s ease'
              }}
            />
          </Box>
        </Box>

        {receivedData?.slug !== "document-classifier" && (
          <div className="menu-button-section" style={{ ...invoiceAgentsStyles.menuButtonSection, ...responsiveStyles.menuButtonSection }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 2 }}>
              <OverviewMenuButtons
                buttons={buttons}
                selected={selected}
                onButtonClick={handleButtonClick}
                containerStyles={{ ...buttonStyles.container, ...responsiveStyles.overviewMenuButtons?.container?.(isEditMode) }}
                buttonStyles={{ ...buttonStyles.button, ...responsiveStyles.overviewMenuButtons?.button?.(isEditMode) }}
              />

              {/* Run Agent Button - Only show in Review tab */}
              {selected === "Review" && !receivedData?.steps && (
                <Button
                  variant="contained"
                  onClick={handleRunClick}
                  sx={{
                    backgroundColor: '#1976D2',
                    color: 'white',
                    textTransform: 'none',
                    borderRadius: '25px',
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    marginRight: 5,
                    fontWeight: 'bold',
                    fontSize: { xs: '14px', sm: '16px' },
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1565C0',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    minWidth: { xs: '100px', sm: '120px' },
                  }}
                >
                  Run Agent
                </Button>
              )}
            </Box>
          </div>
        )}

        {receivedData?.slug !== "document-classifier" && (
          <>
            {/* Blue Tile and Prompts Row - Prompts aligned with Overview Menu */}
            <Box style={{ ...invoiceAgentsStyles.mainContent, ...responsiveStyles.mainContent }}>
              {/* Left Content Area - Blue Tile */}
              <Box style={{ ...invoiceAgentsStyles.leftContent, ...responsiveStyles.leftContent }}>
                <BlueTile
                  title={receivedData?.steps ? (statisticInfo?.data?.name || receivedData?.name || 'Agent') : (receivedData?.name || 'New Agent')}
                  subtitle={receivedData?.steps
                    ? `${statisticInfo?.data?.name || receivedData?.name || 'Agent'} Agent\n${statisticInfo?.data?.name || receivedData?.name || 'Agent'}@docketry.ai\nStatus: ${statisticInfo?.data?.status || 'Sandbox'}`
                    : `${receivedData?.name || 'New Agent'} Agent\n${receivedData?.name || 'New Agent'}@docketry.ai\nStatus: Creating`
                  }
                  imageSrc={agentImageSrc}
                  containerStyles={{ ...blueTileStyles.container, ...responsiveStyles.blueTile?.container?.(receivedData) }}
                  imageStyles={blueTileStyles.image}
                  isLoading={statisticInfoLoading && receivedData?.steps}
                />
              </Box>

              {/* Right Content Area - Prompts (aligned with overview menu) */}
              <Box style={{ ...invoiceAgentsStyles.rightContent, ...responsiveStyles.rightContent }}>
                {selected === "Fields" && (
                  <div >
                    {getCsvLoading && receivedData?.steps ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <Typography variant="h6" sx={{ color: '#666' }}>
                          Loading prompts...
                        </Typography>
                      </Box>
                    ) : (
                      <Prompts error={error} setErrors={setErrors} parentRef={parentRef} />
                    )}
                  </div>
                )}

                {selected === "Review" && (
                  <div style={invoiceAgentsStyles.reviewSection}>
                    {receivedData?.slug !== 'moad-so' || receivedData?.slug !== 'extract-iq' ? (
                      <OutputReview />
                    ) : (
                      <CsvReviewScreen />
                    )}
                  </div>
                )}
              </Box>
            </Box>

            {/* Content Below Blue Tile and Prompts */}
            <Box style={{ ...invoiceAgentsStyles.bottomContent, ...responsiveStyles.bottomContent }}>
              {selected === "Input" && <InputDirectoryForm />}
              {selected === "Fields" && (
                getCsvLoading && receivedData?.steps ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Typography variant="h6" sx={{ color: '#666' }}>
                      Loading agent data...
                    </Typography>
                  </Box>
                ) : (
                  <FieldsDirectoryScreen slug={receivedData?.slug} error={error} setError={setErrors} />
                )
              )}
              {selected === "Output" && <OutPutDirectory />}
              {selected === "Review" && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 3 },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  mt: { xs: 2, md: 1 }
                }}>
                  <ReviewScreen slug={receivedData?.slug} />
                </Box>
              )}
            </Box>
          </>
        )}

        {receivedData?.slug === "document-classifier" && (
          <div style={invoiceAgentsStyles.formSection}>
            <DocumentUpload />
          </div>
        )}

        {receivedData?.slug !== "document-classifier" && (
          <FooterButtons
            selected={selected}
            buttons={buttons}
            receivedData={receivedData}
            createAgentLoading={createAgentLoading}
            handleBackButton={handleBackButton}
            validateAndProceed={validateAndProceed}
            setAgenNamePopUp={setAgenNamePopUp}
            backDisabled={backDisabled}
          />
        )}
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={invoiceAgentsStyles.snackbar.anchorOrigin}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DeployAIBot
        open={dialogOpen}
        onClose={handleDialogClose}
        onDeployStart={() => setShowDeployLoader(true)}
      />
      <AgenName
        open={AgenNamePopUp}
        onClose={() => setAgenNamePopUp(false)}
        onclick={handleCreateAgent}
      />
      <CreateAgentLoader
        open={showCreateLoader}
        onClose={() => setShowCreateLoader(false)}
        mode="create"
      />
      <CreateAgentLoader
        open={showDeployLoader}
        onClose={() => setShowDeployLoader(false)}
        mode="deploy"
      />
    </AppLayout>
  );
};

export default OverviewMenuButtonsWithBorder;
